import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { AlertTriangle, Mail, Inbox } from 'lucide-react';

const fmt = (n) => (n != null && n !== '' ? `₹${Number(n).toLocaleString('en-IN')}` : null);

// ── Confidence banner config ──────────────────────────────────────────────────

const CONF = {
  High: {
    bg: '#f0fdf4', border: '#86efac', barBg: '#16a34a',
    text: '#15803d', label: 'High Confidence', icon: '✓',
  },
  Medium: {
    bg: '#fffbeb', border: '#fcd34d', barBg: '#d97706',
    text: '#92400e', label: 'Medium Confidence', icon: '~',
  },
  Low: {
    bg: '#fff1f2', border: '#fca5a5', barBg: '#dc2626',
    text: '#991b1b', label: 'Low Confidence', icon: '!',
  },
};

// ── DraftField — small labeled tile, amber if empty and required ──────────────

function DraftField({ label, value, required = false, fullWidth = false }) {
  const empty = required && (value === null || value === undefined || value === '' || value === 0);
  return (
    <div style={{
      gridColumn: fullWidth ? '1 / -1' : undefined,
      background: empty ? '#fffbeb' : 'var(--color-sunken)',
      border: `1px solid ${empty ? '#fcd34d' : 'var(--color-border)'}`,
      borderRadius: 8,
      padding: '8px 12px',
      minWidth: 0,
    }}>
      <p style={{
        fontSize: '0.64rem', fontWeight: 700, color: empty ? '#b45309' : 'var(--color-ink-muted)',
        textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 3,
      }}>
        {label}{empty && <AlertTriangle size={12} strokeWidth={1.75} aria-hidden="true" style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: 3 }} />}
      </p>
      <p style={{
        fontSize: '0.88rem', fontWeight: 600,
        color: empty ? '#92400e' : '#0A1628',
        overflowWrap: 'break-word', wordBreak: 'break-word',
      }}>
        {(value !== null && value !== undefined && value !== '') ? String(value) : '—'}
      </p>
    </div>
  );
}

// ── Single draft card ─────────────────────────────────────────────────────────

function DraftCard({ draft, onConfirm, onDiscard }) {
  const [emailOpen, setEmailOpen]       = useState(false);
  const [discardPrompt, setDiscardPrompt] = useState(false);
  const [discarding, setDiscarding]     = useState(false);

  const conf = CONF[draft.ai_confidence] || CONF.Medium;

  const handleDiscard = async () => {
    setDiscarding(true);
    await supabase
      .from('draft_inquiries')
      .update({ status: 'discarded' })
      .eq('id', draft.id);
    setDiscarding(false);
    onDiscard(draft.id);
  };

  return (
    <div style={{
      background: '#ffffff',
      border: '1px solid var(--color-border)',
      borderRadius: 14,
      marginBottom: 16,
      overflow: 'hidden',
      boxShadow: '0 1px 6px rgba(10,22,40,0.06)',
    }}>
      {/* ① Confidence banner */}
      <div style={{
        background: conf.bg,
        borderBottom: `1px solid ${conf.border}`,
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}>
        <span style={{
          width: 26, height: 26, borderRadius: '50%',
          background: conf.barBg,
          color: '#fff', fontSize: '0.8rem', fontWeight: 800,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          {conf.icon}
        </span>
        <div>
          <p style={{ fontSize: '0.82rem', fontWeight: 700, color: conf.text }}>
            {conf.label}
          </p>
          <p style={{ fontSize: '0.7rem', color: conf.text, opacity: 0.75 }}>
            AI-extracted from email
          </p>
        </div>
      </div>

      <div style={{ padding: '14px 16px' }}>
        {/* Booking name as heading */}
        <p style={{
          fontSize: '1rem', fontWeight: 800, color: '#0A1628',
          marginBottom: draft.sender_email ? 4 : 12, overflowWrap: 'break-word',
        }}>
          {draft.booking_name || <span style={{ color: '#b45309' }}>Name not found</span>}
        </p>

        {draft.sender_email && (
          <p style={{
            fontSize: '0.75rem', color: 'var(--color-ink-muted)', fontWeight: 500,
            marginBottom: 12, overflowWrap: 'break-word',
          }}>
            From: {draft.sender_email}
          </p>
        )}

        {/* ② Raw email — collapsible */}
        {draft.raw_email && (
          <div style={{
            background: 'var(--color-sunken)', border: '1px solid var(--color-border)',
            borderRadius: 8, marginBottom: 14, overflow: 'hidden',
          }}>
            <button
              onClick={() => setEmailOpen(!emailOpen)}
              style={{
                width: '100%', background: 'none', border: 'none',
                padding: '9px 14px', cursor: 'pointer',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                fontSize: '0.78rem', fontWeight: 600, color: '#4a5a7a',
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Mail size={16} strokeWidth={1.75} aria-hidden="true" /> Original email</span>
              <span style={{ fontSize: '0.7rem', color: 'var(--color-ink-faint)' }}>
                {emailOpen ? 'Hide ▲' : 'Show ▼'}
              </span>
            </button>
            {emailOpen && (
              <div style={{
                borderTop: '1px solid var(--color-border)',
                padding: '12px 14px',
                maxHeight: 240,
                overflowY: 'auto',
              }}>
                <pre style={{
                  fontFamily: 'inherit', fontSize: '0.78rem', color: '#2a3a5a',
                  lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  margin: 0,
                }}>
                  {draft.raw_email}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* ③ Extracted fields grid */}
        <div className="detail-grid" style={{ gap: 8, marginBottom: 16 }}>
          <DraftField label="Date"         value={draft.date}         required />
          <DraftField label="Cruise Type"  value={draft.cruise_type}  required />
          <DraftField label="Deck"         value={draft.deck}         required />
          <DraftField label="Adults"       value={draft.adults}       required />
          <DraftField label="Children"     value={draft.children === 0 ? '0' : draft.children} />
          <DraftField label="Partner Name" value={draft.partner_name} />
          <DraftField label="Phone"        value={draft.phone_number} />
          <DraftField label="Total"        value={fmt(draft.total_amount)} required />
          <DraftField label="TAC"          value={fmt(draft.tac)} />
          <DraftField label="Net Total"    value={fmt(draft.net_total)} />
          <DraftField label="Payment Mode" value={draft.payment_mode} />
          <DraftField label="Payment Status" value={draft.payment_status} />
          {draft.notes && (
            <DraftField label="Notes" value={draft.notes} fullWidth />
          )}
        </div>

        {/* ④ Actions */}
        {!discardPrompt ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              className="btn btn-gold"
              style={{ flex: '1 1 140px' }}
              onClick={() => onConfirm(draft)}
            >
              ✓ Review &amp; Confirm
            </button>
            <button
              className="btn btn-danger"
              style={{ flex: '0 0 auto' }}
              onClick={() => setDiscardPrompt(true)}
            >
              Discard
            </button>
          </div>
        ) : (
          <div style={{
            background: '#fff1f2', border: '1px solid #fca5a5',
            borderRadius: 10, padding: '12px 14px',
          }}>
            <p style={{ fontSize: '0.84rem', fontWeight: 600, color: '#991b1b', marginBottom: 10 }}>
              Discard this inquiry? It will be hidden from this list but kept in the database.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-sm btn-danger"
                onClick={handleDiscard}
                disabled={discarding}
              >
                {discarding ? 'Discarding…' : 'Yes, Discard'}
              </button>
              <button
                className="btn btn-sm btn-outline"
                onClick={() => setDiscardPrompt(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main screen ───────────────────────────────────────────────────────────────

export default function DraftInquiries({ onNavigate }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('draft_inquiries')
      .select('*')
      .eq('status', 'unreviewed')
      .order('created_at', { ascending: false });
    setDrafts(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  const handleDiscard = (id) => setDrafts((prev) => prev.filter((d) => d.id !== id));

  return (
    <div>
      <div className="back-row">
        <button className="back-btn" onClick={() => onNavigate('home')}>← Back</button>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#0A1628' }}>
            Email Inquiries
          </h2>
          <p style={{ fontSize: '0.73rem', color: 'var(--color-ink-muted)', marginTop: 1 }}>
            Review AI-extracted bookings before confirming
          </p>
        </div>
        {drafts.length > 0 && (
          <span style={{
            background: '#f59e0b', color: '#fff',
            borderRadius: 20, padding: '2px 10px',
            fontSize: '0.78rem', fontWeight: 700, flexShrink: 0,
          }}>
            {drafts.length}
          </span>
        )}
      </div>

      {loading ? (
        <p style={{ color: 'var(--color-ink-faint)', fontSize: '0.9rem' }}>Loading…</p>
      ) : drafts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '56px 24px', color: 'var(--color-ink-faint)' }}>
          <div style={{ marginBottom: 10 }}><Inbox size={32} strokeWidth={1.75} aria-hidden="true" /></div>
          <p style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: 4 }}>All caught up</p>
          <p style={{ fontSize: '0.82rem' }}>No unreviewed email inquiries</p>
        </div>
      ) : (
        <div>
          {drafts.map((draft) => (
            <DraftCard
              key={draft.id}
              draft={draft}
              onConfirm={(d) => onNavigate('review-draft', d.date, d)}
              onDiscard={handleDiscard}
            />
          ))}
        </div>
      )}
    </div>
  );
}
